import { z } from 'zod';

export const testItemDtoSchema = z.object({
  itemId: z.string().nullable(),
  itemName: z.string().nullable()
});
export type TestItemDto = z.infer<typeof testItemDtoSchema>;

export const codeBaseDtoSchema = z.object({
  id: z.string().nullable(),
  codeType: z.string().nullable(),
  code: z.string().nullable(),
  description: z.string().nullable()
});
export type CodeBaseDto = z.infer<typeof codeBaseDtoSchema>;

export const containerDtoSchema = z.object({
  containerId: z.string().nullable(),
  containerName: z.string().nullable(),
  testItems: z.array(testItemDtoSchema).nullable()
});
export type ContainerDto = z.infer<typeof containerDtoSchema>;

export const specimenDtoSchema = z.object({
  code: z.string().nullable(),
  name: z.string().nullable(),
  instruction: z.string().nullable()
});
export type SpecimenDto = z.infer<typeof specimenDtoSchema>;

export const durationDtoSchema = z.object({
  value: z.number().nullable(),
  unit: z.string().nullable()
});
export type DurationDto = z.infer<typeof durationDtoSchema>;

export const dosageDtoSchema = z.object({
  amount: z.number().nullable(),
  unit: z.string().nullable()
});
export type DosageDto = z.infer<typeof dosageDtoSchema>;

export const staffDtoSchema = z.object({
  doctorInChargeName: z.string().nullable(),
  doctorInChargeId: z.string().nullable(),
  doctorInChargeCode: z.string().nullable(),
  nurseInChargeName: z.string().nullable(),
  nurseInChargeId: z.string().nullable(),
  nurseInChargeCode: z.string().nullable(),
  headNurseName: z.string().nullable(),
  headNurseId: z.string().nullable(),
  headNurseCode: z.string().nullable(),
  departmentHeadDoctorName: z.string().nullable(),
  departmentHeadDoctorId: z.string().nullable(),
  departmentHeadDoctorCode: z.string().nullable()
});
export type StaffDto = z.infer<typeof staffDtoSchema>;

export const specialStatusDtoSchema = z.object({
  patientSpecialCondition: z.enum(["NORMAL", "URGENT", "HIGH_RISK", "ATTENTION"]).nullable(),
  note: z.string().nullable()
});
export type SpecialStatusDto = z.infer<typeof specialStatusDtoSchema>;

export const vitalsDtoSchema = z.object({
  id: z.string().nullable(),
  observationCode: z.enum(["BP_SYS", "BP_DIA", "HEART_RATE", "SPO2", "BODY_TEMP", "RESP_RATE", "BODY_HEIGHT", "BODY_WEIGHT", "BMI", "PAIN_SCORE", "MAP", "GCS_EYE", "GCS_VERBAL", "GCS_MOTOR", "MUSCLE_POWER_LEFT_ARM", "MUSCLE_POWER_RIGHT_ARM", "MUSCLE_POWER_LEFT_LEG", "MUSCLE_POWER_RIGHT_LEG", "PUPIL_SIZE_LEFT", "PUPIL_SIZE_RIGHT", "IN_BLOOD", "IN_FOOD", "IN_INJECT", "IN_WATER", "IN_OTHER_LIQ", "IN_OTHER_SOLID", "IN_TPN", "IN_ORAL", "OUT_URINE", "OUT_STOOL", "OUT_DRAIN_1", "OUT_DRAIN_2", "OUT_DRAIN_3", "OUT_OTHER", "UTERINE_FIRMNESS", "FUNDAL_HEIGHT", "LOCHIA_AMOUNT", "LOCHIA_COLOR", "LOCHIA_ODOR", "WOUND_TYPE", "PERINEAL_IRRIGATION", "WOUND_CONDITION", "NIPPLE_APPEARANCE", "BREASTFEEDING_STATUS"]).nullable(),
  effectiveDatetime: z.string().nullable(),
  value: z.string().nullable(),
  codeBaseDto: codeBaseDtoSchema.nullable()
});
export type VitalsDto = z.infer<typeof vitalsDtoSchema>;

export const cervicalExamDtoSchema = z.object({
  station: z.enum(["ZERO_STATION", "MINUS_3_TO_PLUS_3", "FLOATING"]).nullable(),
  effacement: z.enum(["POOR", "P30", "P50", "P70", "P80", "P90", "P100_THIN"]).nullable(),
  dilated: z.enum(["CLOSE", "CM_1_TO_9", "FULL", "NF"]).nullable(),
  membraneStatus: z.enum(["I", "R"]).nullable()
});
export type CervicalExamDto = z.infer<typeof cervicalExamDtoSchema>;

export const uterusDtoSchema = z.object({
  contractionFreq: z.number().nullable(),
  contractionDuration: z.number().nullable(),
  note: z.string().nullable()
});
export type UterusDto = z.infer<typeof uterusDtoSchema>;

export const presentationDtoSchema = z.object({
  type: z.enum(["VX", "BR", "TRANSVERSE"]).nullable(),
  note: z.string().nullable()
});
export type PresentationDto = z.infer<typeof presentationDtoSchema>;

export const labReportDetailDtoSchema = z.object({
  labReportDetailId: z.string().nullable(),
  labReportId: z.string().nullable(),
  itemCode: z.string().nullable(),
  itemNameEn: z.string().nullable(),
  itemNameZh: z.string().nullable(),
  resultType: z.string().nullable(),
  valueUnit: z.string().nullable(),
  resultValue: z.string().nullable(),
  resultStatus: z.string().nullable(),
  normalRangeLow: z.number().nullable(),
  normalRangeHigh: z.number().nullable(),
  normalText: z.string().nullable(),
  abnormalFlag: z.string().nullable(),
  sortSeq: z.number().nullable(),
  patientId: z.string().nullable(),
  effectiveDatetime: z.string().nullable(),
  note: z.string().nullable(),
  resultStatusI18n: z.string().nullable()
});
export type LabReportDetailDto = z.infer<typeof labReportDetailDtoSchema>;

export const orderItemDtoSchema = z.object({
  orderId: z.string().nullable(),
  orderType: z.enum(["MEDICATION", "PRN_MEDICATION", "INJECTION", "LAB", "EXAM", "SURGERY"]).nullable(),
  orderStatus: z.enum(["ACTIVE", "ORDERED", "OVERTIME", "COMPLETED", "PROCESSED", "CANCELLED"]).nullable(),
  itemId: z.string().nullable(),
  itemName: z.string().nullable(),
  form: z.string().nullable(),
  dosage: dosageDtoSchema.nullable(),
  route: z.string().nullable(),
  frequency: z.string().nullable(),
  timing: z.string().nullable(),
  duration: durationDtoSchema.nullable(),
  performDatetime: z.string().nullable(),
  modifyUser: z.string().nullable(),
  modifyUserName: z.string().nullable(),
  recordUser: z.string().nullable(),
  recordUserName: z.string().nullable(),
  recordDatetime: z.string().nullable(),
  orderStartDatetime: z.string().nullable(),
  orderEndDatetime: z.string().nullable(),
  sideEffectCode: z.string().nullable(),
  sideEffectVital: z.string().nullable(),
  notActiveReasonCode: z.string().nullable(),
  notActiveReasonMemo: z.string().nullable(),
  cancelReason: z.string().nullable(),
  prnActiveReason: z.string().nullable(),
  specimen: specimenDtoSchema.nullable(),
  containers: z.array(containerDtoSchema).nullable()
});
export type OrderItemDto = z.infer<typeof orderItemDtoSchema>;

export const medicationDetailDtoSchema = z.object({
  itemName: z.string().nullable(),
  dosage: dosageDtoSchema.nullable(),
  route: z.string().nullable(),
  frequency: z.string().nullable(),
  timing: z.string().nullable(),
  duration: durationDtoSchema.nullable(),
  mixItemNameList: z.array(z.string()).nullable()
});
export type MedicationDetailDto = z.infer<typeof medicationDetailDtoSchema>;

export const actionDtoSchema = z.object({
  orderType: z.enum(["MEDICATION", "PRN_MEDICATION", "INJECTION", "LAB", "EXAM", "SURGERY"]).nullable(),
  orderStatus: z.enum(["ACTIVE", "ORDERED", "OVERTIME", "COMPLETED", "PROCESSED", "CANCELLED"]).nullable(),
  actionDescription: z.string().nullable()
});
export type ActionDto = z.infer<typeof actionDtoSchema>;

export const rupturedDetailsReqDtoSchema = z.object({
  type: z.enum(["ARTIFICIAL", "SPONTANEOUS"]).nullable(),
  time: z.string().nullable(),
  color: z.enum(["CLEAR", "MECONIUM", "DARK_RED"]).nullable(),
  odor: z.enum(["NORMAL", "FOUL"]).nullable(),
  amount: z.enum(["LARGE", "MEDIUM", "SMALL"]).nullable(),
  texture: z.enum(["WATERY", "VISCOUS"]).nullable()
});
export type RupturedDetailsReqDto = z.infer<typeof rupturedDetailsReqDtoSchema>;

export const queryPatientResSchema = z.object({
  id: z.string().nullable(),
  patientId: z.string().nullable(),
  encounterId: z.string().nullable(),
  bedNo: z.string().nullable(),
  patientName: z.string().nullable(),
  age: z.number().nullable(),
  mrn: z.string().nullable(),
  gestationalAge: z.string().nullable(),
  gravidaPara: z.string().nullable(),
  laborHours: z.string().nullable(),
  edd: z.string().nullable(),
  presentation: presentationDtoSchema.nullable(),
  fhr: z.number().nullable(),
  uterus: uterusDtoSchema.nullable(),
  cervicalExam: cervicalExamDtoSchema.nullable(),
  vitalList: z.array(vitalsDtoSchema).nullable(),
  admissionTime: z.string().nullable(),
  epiduralActive: z.boolean().nullable(),
  laborStage: z.enum(["WAITING_PHASE", "EARLY_PHASE", "ACTIVE_PHASE", "TRANSITION_PHASE", "PENDING_IN", "TRANSFERRING_OUT"]).nullable(),
  specialStatus: specialStatusDtoSchema.nullable(),
  staff: staffDtoSchema.nullable(),
  transferOutDateTime: z.string().nullable(),
  toBed: z.string().nullable()
});
export type QueryPatientRes = z.infer<typeof queryPatientResSchema>;

export const drainTubeDtoSchema = z.object({
  drainCode: z.string().nullable(),
  leftFlag: z.boolean().nullable(),
  rightFlag: z.boolean().nullable(),
  siteOther: z.string().nullable(),
  memo: z.string().nullable(),
  recordStatus: z.enum(["C", "U", "D", "R"]).nullable(),
  id: z.string().nullable()
});
export type DrainTubeDto = z.infer<typeof drainTubeDtoSchema>;

export const bloodTransfusionDtoSchema = z.object({
  completeFlag: z.boolean().nullable(),
  bloodProductCode: z.string().nullable(),
  bagQty: z.number().nullable(),
  recordStatus: z.enum(["C", "U", "D", "R"]).nullable(),
  id: z.string().nullable()
});
export type BloodTransfusionDto = z.infer<typeof bloodTransfusionDtoSchema>;

export const dripInfoDtoSchema = z.object({
  orderId: z.string().nullable(),
  leftFlag: z.boolean().nullable(),
  rightFlag: z.boolean().nullable(),
  siteOther: z.string().nullable(),
  memo: z.string().nullable(),
  recordStatus: z.enum(["C", "U", "D", "R"]).nullable(),
  id: z.string().nullable(),
  orderName: z.string().nullable(),
  orderStartDatetime: z.string().nullable(),
  orderEndDatetime: z.string().nullable(),
  routeCode: z.string().nullable(),
  freqCode: z.string().nullable(),
  dosageQty: z.string().nullable(),
  dosageUnit: z.string().nullable()
});
export type DripInfoDto = z.infer<typeof dripInfoDtoSchema>;

export const examOrderDtoSchema = z.object({
  orderCode: z.string().nullable(),
  completedFlag: z.boolean().nullable(),
  recordStatus: z.enum(["C", "U", "D", "R"]).nullable(),
  id: z.string().nullable()
});
export type ExamOrderDto = z.infer<typeof examOrderDtoSchema>;

export const bedTransferLabReportDtoSchema = z.object({
  id: z.string().nullable(),
  bedTransferId: z.string().nullable(),
  labReportId: z.string().nullable(),
  reportCategory: z.string().nullable(),
  details: z.array(labReportDetailDtoSchema).nullable()
});
export type BedTransferLabReportDto = z.infer<typeof bedTransferLabReportDtoSchema>;

export const bedTransferDtoSchema = z.object({
  bedTransferId: z.string().nullable(),
  encounterId: z.string().nullable(),
  toBedNo: z.string().nullable(),
  fromBedNo: z.string().nullable(),
  selfPayConsentStatus: z.enum(["NONE", "PENDING", "UNSIGNED", "SIGNED"]).nullable(),
  adHistory: z.string().nullable(),
  pastMedicalHistory: z.string().nullable(),
  adDiagnosis: z.string().nullable(),
  ipDiagnosis: z.string().nullable(),
  procedureName: z.string().nullable(),
  surgeryBodySite: z.string().nullable(),
  specialMedication: z.string().nullable(),
  specialMedicationMemo: z.string().nullable(),
  medicalRecordFlag: z.boolean().nullable(),
  oldMedicalRecordFlag: z.boolean().nullable(),
  status: z.enum(["DRAFT", "CONFIRM", "COMPLETE"]).nullable(),
  statusDisplay: z.string().nullable(),
  tenantId: z.string().nullable(),
  transferUser: z.string().nullable(),
  transferDatetime: z.string().nullable(),
  receiverUser: z.string().nullable(),
  receiveDatetime: z.string().nullable(),
  bedAddFee: z.number().nullable(),
  createUserId: z.string().nullable(),
  createDatetime: z.string().nullable(),
  updateUserId: z.string().nullable(),
  updateDatetime: z.string().nullable(),
  lockVersion: z.number().nullable()
});
export type BedTransferDto = z.infer<typeof bedTransferDtoSchema>;

export const queryEncounterCardResSchema = z.object({
  id: z.string().nullable(),
  encounterType: z.string().nullable(),
  visitStartDatetime: z.string().nullable(),
  visitEndDatetime: z.string().nullable(),
  doctorName: z.string().nullable(),
  divName: z.string().nullable(),
  icdCode: z.string().nullable(),
  diagnosisDesc: z.string().nullable(),
  allergenNames: z.array(z.string()).nullable(),
  roomNo: z.string().nullable(),
  bedNo: z.string().nullable(),
  seqNo: z.string().nullable(),
  bodyWeight: z.number().nullable(),
  effectiveDatetime: z.string().nullable(),
  surgeryDatetimeList: z.array(z.string()).nullable()
});
export type QueryEncounterCardRes = z.infer<typeof queryEncounterCardResSchema>;

export const bedResSchema = z.object({
  id: z.string().nullable(),
  bedNo: z.string().nullable(),
  roomId: z.string().nullable(),
  roomNo: z.string().nullable(),
  stationId: z.string().nullable(),
  stationName: z.string().nullable(),
  bedType: z.string().nullable(),
  bedClass: z.string().nullable(),
  bedAddFee: z.number().nullable(),
  gender: z.enum(["male", "female", "other", "unknown"]).nullable(),
  encounterId: z.string().nullable(),
  patientId: z.string().nullable(),
  mrn: z.string().nullable(),
  status: z.enum(["active", "empty", "pending"]).nullable(),
  sameRoomGender: z.array(z.enum(["male", "female", "other", "unknown"])).nullable(),
  sameRoomGenderI18n: z.array(z.string()).nullable(),
  genderI18n: z.string().nullable()
});
export type BedRes = z.infer<typeof bedResSchema>;

export const roomMateResSchema = z.object({
  id: z.string().nullable(),
  bedNo: z.string().nullable(),
  patientId: z.string().nullable(),
  patientName: z.string().nullable(),
  encounterId: z.string().nullable(),
  gender: z.enum(["male", "female", "other", "unknown"]).nullable(),
  status: z.enum(["active", "empty", "pending"]).nullable(),
  mrn: z.string().nullable(),
  staffDto: staffDtoSchema.nullable(),
  genderI18n: z.string().nullable()
});
export type RoomMateRes = z.infer<typeof roomMateResSchema>;

export const locationDtoSchema = z.object({
  locationId: z.string().nullable(),
  locationCode: z.string().nullable(),
  locationName: z.string().nullable(),
  physicalTypeCode: z.string().nullable(),
  parentLocationId: z.string().nullable()
});
export type LocationDto = z.infer<typeof locationDtoSchema>;

export const queryOrderResSchema = z.object({
  id: z.string().nullable(),
  encounterId: z.string().nullable(),
  patientId: z.string().nullable(),
  orderGroupId: z.string().nullable(),
  description: z.string().nullable(),
  createUser: z.string().nullable(),
  createUserName: z.string().nullable(),
  createDatetime: z.string().nullable(),
  orderStatus: z.enum(["ACTIVE", "ORDERED", "OVERTIME", "COMPLETED", "PROCESSED", "CANCELLED"]).nullable(),
  orders: z.array(orderItemDtoSchema).nullable()
});
export type QueryOrderRes = z.infer<typeof queryOrderResSchema>;

export const queryHistoryResSchema = z.object({
  encounterId: z.string().min(1),
  performDatetime: z.string().min(1),
  presentation: presentationDtoSchema.nullable(),
  fhr: z.number().nullable(),
  uterus: uterusDtoSchema.nullable(),
  cervicalExam: cervicalExamDtoSchema.nullable(),
  rupturedDetails: rupturedDetailsReqDtoSchema.nullable(),
  laborStage: z.enum(["WAITING_PHASE", "EARLY_PHASE", "ACTIVE_PHASE", "TRANSITION_PHASE", "PENDING_IN", "TRANSFERRING_OUT"]).nullable(),
  id: z.string().nullable(),
  type: z.enum(["DOCTOR_ORDER", "NURSE_ORDER", "DELIVERY_PROCESS"]).nullable(),
  staff: staffDtoSchema.nullable(),
  description: z.string().nullable(),
  lockVersion: z.number().nullable(),
  action: actionDtoSchema.nullable(),
  vitalList: z.array(vitalsDtoSchema).nullable(),
  medicationDetail: medicationDetailDtoSchema.nullable()
});
export type QueryHistoryRes = z.infer<typeof queryHistoryResSchema>;

export const nursingNoteDtoSchema = z.object({
  id: z.string().nullable(),
  content: z.string().nullable(),
  performTime: z.string().nullable(),
  source: z.enum(["MANUAL", "AUTO"]).nullable(),
  createUser: z.string().nullable(),
  createUserName: z.string().nullable(),
  createDatetime: z.string().nullable(),
  modifyUser: z.string().nullable(),
  modifyUserName: z.string().nullable(),
  modifyDatetime: z.string().nullable()
});
export type NursingNoteDto = z.infer<typeof nursingNoteDtoSchema>;

export const queryPatientCardResSchema = z.object({
  patientId: z.string().nullable(),
  mrn: z.string().nullable(),
  patientName: z.string().nullable(),
  gender: z.string().nullable(),
  birthDate: z.string().nullable(),
  catastrophicIllnessFlag: z.boolean().nullable(),
  dnrFlag: z.boolean().nullable(),
  alertFlag: z.boolean().nullable(),
  allergyFlag: z.boolean().nullable(),
  isolationFlag: z.boolean().nullable(),
  fastingFlag: z.boolean().nullable(),
  icCardRemarkList: z.array(z.string()).nullable(),
  tenantId: z.string().nullable(),
  genderI18n: z.string().nullable()
});
export type QueryPatientCardRes = z.infer<typeof queryPatientCardResSchema>;

export const queryPatientListResSchema = z.object({
  timestamp: z.number().nullable(),
  patients: z.array(queryPatientResSchema).nullable()
});
export type QueryPatientListRes = z.infer<typeof queryPatientListResSchema>;

export const queryPatientSummaryResSchema = z.object({
  timestamp: z.number().nullable(),
  urgent: z.number().nullable(),
  highRisk: z.number().nullable(),
  attention: z.number().nullable(),
  waitingStage: z.number().nullable(),
  earlyStage: z.number().nullable(),
  activeStage: z.number().nullable(),
  transitionStage: z.number().nullable()
});
export type QueryPatientSummaryRes = z.infer<typeof queryPatientSummaryResSchema>;

export const queryDoctorResSchema = z.object({
  practitionerId: z.string().nullable(),
  practitionerCode: z.string().nullable(),
  practitionerName: z.string().nullable()
});
export type QueryDoctorRes = z.infer<typeof queryDoctorResSchema>;

export const queryBedTransferResSchema = z.object({
  bedTransfer: bedTransferDtoSchema.nullable(),
  labReports: z.array(bedTransferLabReportDtoSchema).nullable(),
  examOrders: z.array(examOrderDtoSchema).nullable(),
  drips: z.array(dripInfoDtoSchema).nullable(),
  bloodTransfusions: z.array(bloodTransfusionDtoSchema).nullable(),
  drainageTubes: z.array(drainTubeDtoSchema).nullable()
});
export type QueryBedTransferRes = z.infer<typeof queryBedTransferResSchema>;

export const queryCheckRecordResSchema = z.object({
  checkField: z.string().nullable(),
  content: z.unknown().nullable(),
  checkerId: z.string().nullable(),
  checkerName: z.string().nullable(),
  createDatetime: z.string().nullable()
});
export type QueryCheckRecordRes = z.infer<typeof queryCheckRecordResSchema>;

export const queryTenantCodeBaseResSchema = z.object({
  id: z.string().nullable(),
  codeType: z.string().nullable(),
  code: z.string().nullable(),
  description: z.string().nullable()
});
export type QueryTenantCodeBaseRes = z.infer<typeof queryTenantCodeBaseResSchema>;

export const addVitalReqSchema = z.object({
  observationCode: z.enum(["BP_SYS", "BP_DIA", "HEART_RATE", "SPO2", "BODY_TEMP", "RESP_RATE", "BODY_HEIGHT", "BODY_WEIGHT", "BMI", "PAIN_SCORE", "MAP", "GCS_EYE", "GCS_VERBAL", "GCS_MOTOR", "MUSCLE_POWER_LEFT_ARM", "MUSCLE_POWER_RIGHT_ARM", "MUSCLE_POWER_LEFT_LEG", "MUSCLE_POWER_RIGHT_LEG", "PUPIL_SIZE_LEFT", "PUPIL_SIZE_RIGHT", "IN_BLOOD", "IN_FOOD", "IN_INJECT", "IN_WATER", "IN_OTHER_LIQ", "IN_OTHER_SOLID", "IN_TPN", "IN_ORAL", "OUT_URINE", "OUT_STOOL", "OUT_DRAIN_1", "OUT_DRAIN_2", "OUT_DRAIN_3", "OUT_OTHER", "UTERINE_FIRMNESS", "FUNDAL_HEIGHT", "LOCHIA_AMOUNT", "LOCHIA_COLOR", "LOCHIA_ODOR", "WOUND_TYPE", "PERINEAL_IRRIGATION", "WOUND_CONDITION", "NIPPLE_APPEARANCE", "BREASTFEEDING_STATUS"]).nullable(),
  effectiveDatetime: z.string().nullable(),
  value: z.string().nullable(),
  codeBaseDto: codeBaseDtoSchema.nullable()
});
export type AddVitalReq = z.infer<typeof addVitalReqSchema>;

export const addBedTransferResSchema = z.object({
  bedTransferId: z.string().nullable(),
  msg: z.string().nullable()
});
export type AddBedTransferRes = z.infer<typeof addBedTransferResSchema>;

export const drainTubeCreateDtoSchema = z.object({
  drainCode: z.string().nullable(),
  leftFlag: z.boolean().nullable(),
  rightFlag: z.boolean().nullable(),
  siteOther: z.string().nullable(),
  memo: z.string().nullable(),
  recordStatus: z.enum(["C", "U", "D", "R"]).nullable()
});
export type DrainTubeCreateDto = z.infer<typeof drainTubeCreateDtoSchema>;

export const bloodTransfusionCreateDtoSchema = z.object({
  completeFlag: z.boolean().nullable(),
  bloodProductCode: z.string().nullable(),
  bagQty: z.number().nullable(),
  recordStatus: z.enum(["C", "U", "D", "R"]).nullable()
});
export type BloodTransfusionCreateDto = z.infer<typeof bloodTransfusionCreateDtoSchema>;

export const dripInfoCreateDtoSchema = z.object({
  orderId: z.string().nullable(),
  leftFlag: z.boolean().nullable(),
  rightFlag: z.boolean().nullable(),
  siteOther: z.string().nullable(),
  memo: z.string().nullable(),
  recordStatus: z.enum(["C", "U", "D", "R"]).nullable()
});
export type DripInfoCreateDto = z.infer<typeof dripInfoCreateDtoSchema>;

export const examOrderCreateDtoSchema = z.object({
  orderCode: z.string().nullable(),
  completedFlag: z.boolean().nullable(),
  recordStatus: z.enum(["C", "U", "D", "R"]).nullable()
});
export type ExamOrderCreateDto = z.infer<typeof examOrderCreateDtoSchema>;

export const updateOrderExecutedResSchema = z.object({
  modifyUser: z.string().nullable(),
  modifyUserName: z.string().nullable(),
  orderStartDatetime: z.string().nullable(),
  orderEndDatetime: z.string().nullable(),
  performDatetime: z.string().nullable(),
  orderId: z.string().nullable(),
  orderStatus: z.enum(["ACTIVE", "ORDERED", "OVERTIME", "COMPLETED", "PROCESSED", "CANCELLED"]).nullable(),
  sideEffectCode: z.string().nullable(),
  sideEffectVital: z.string().nullable(),
  notActiveReasonCode: z.string().nullable(),
  notActiveReasonMemo: z.string().nullable(),
  cancelReason: z.string().nullable(),
  prnActiveReason: z.string().nullable(),
  orderGroupId: z.string().nullable(),
  recordUser: z.string().nullable(),
  recordUserName: z.string().nullable(),
  recordDatetime: z.string().nullable()
});
export type UpdateOrderExecutedRes = z.infer<typeof updateOrderExecutedResSchema>;

export const updateBedTransferResSchema = z.object({
  bedTransferId: z.string().nullable(),
  msg: z.string().nullable()
});
export type UpdateBedTransferRes = z.infer<typeof updateBedTransferResSchema>;

export const dripInfoUpdateDtoSchema = z.object({
  orderId: z.string().nullable(),
  leftFlag: z.boolean().nullable(),
  rightFlag: z.boolean().nullable(),
  siteOther: z.string().nullable(),
  memo: z.string().nullable(),
  recordStatus: z.enum(["C", "U", "D", "R"]).nullable(),
  id: z.string().nullable()
});
export type DripInfoUpdateDto = z.infer<typeof dripInfoUpdateDtoSchema>;

export const apiPageResponseObjectSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.unknown().nullable(),
  totalPageSize: z.number().nullable(),
  totalItemSize: z.number().nullable(),
  currentPageItemSize: z.number().nullable(),
  pageSize: z.number().nullable()
});
export type ApiPageResponseObject = z.infer<typeof apiPageResponseObjectSchema>;

export const apiResponseQueryEncounterCardResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: queryEncounterCardResSchema.nullable()
});
export type ApiResponseQueryEncounterCardRes = z.infer<typeof apiResponseQueryEncounterCardResSchema>;

export const apiResponseListBedResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(bedResSchema).nullable()
});
export type ApiResponseListBedRes = z.infer<typeof apiResponseListBedResSchema>;

export const apiResponseListRoomMateResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(roomMateResSchema).nullable()
});
export type ApiResponseListRoomMateRes = z.infer<typeof apiResponseListRoomMateResSchema>;

export const apiResponseListLocationDtoSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(locationDtoSchema).nullable()
});
export type ApiResponseListLocationDto = z.infer<typeof apiResponseListLocationDtoSchema>;

export const apiResponseQueryOrderResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: queryOrderResSchema.nullable()
});
export type ApiResponseQueryOrderRes = z.infer<typeof apiResponseQueryOrderResSchema>;

export const apiResponseListOrderItemDtoSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(orderItemDtoSchema).nullable()
});
export type ApiResponseListOrderItemDto = z.infer<typeof apiResponseListOrderItemDtoSchema>;

export const apiResponseListQueryOrderResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(queryOrderResSchema).nullable()
});
export type ApiResponseListQueryOrderRes = z.infer<typeof apiResponseListQueryOrderResSchema>;

export const apiResponseQueryHistoryResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: queryHistoryResSchema.nullable()
});
export type ApiResponseQueryHistoryRes = z.infer<typeof apiResponseQueryHistoryResSchema>;

export const apiResponseListQueryHistoryResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(queryHistoryResSchema).nullable()
});
export type ApiResponseListQueryHistoryRes = z.infer<typeof apiResponseListQueryHistoryResSchema>;

export const apiResponseNursingNoteDtoSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: nursingNoteDtoSchema.nullable()
});
export type ApiResponseNursingNoteDto = z.infer<typeof apiResponseNursingNoteDtoSchema>;

export const apiResponseListNursingNoteDtoSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(nursingNoteDtoSchema).nullable()
});
export type ApiResponseListNursingNoteDto = z.infer<typeof apiResponseListNursingNoteDtoSchema>;

export const apiResponseQueryPatientResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: queryPatientResSchema.nullable()
});
export type ApiResponseQueryPatientRes = z.infer<typeof apiResponseQueryPatientResSchema>;

export const apiResponseQueryPatientCardResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: queryPatientCardResSchema.nullable()
});
export type ApiResponseQueryPatientCardRes = z.infer<typeof apiResponseQueryPatientCardResSchema>;

export const apiResponseQueryPatientListResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: queryPatientListResSchema.nullable()
});
export type ApiResponseQueryPatientListRes = z.infer<typeof apiResponseQueryPatientListResSchema>;

export const apiResponseQueryPatientSummaryResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: queryPatientSummaryResSchema.nullable()
});
export type ApiResponseQueryPatientSummaryRes = z.infer<typeof apiResponseQueryPatientSummaryResSchema>;

export const apiResponseListQueryDoctorResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(queryDoctorResSchema).nullable()
});
export type ApiResponseListQueryDoctorRes = z.infer<typeof apiResponseListQueryDoctorResSchema>;

export const apiResponseListQueryBedTransferResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(queryBedTransferResSchema).nullable()
});
export type ApiResponseListQueryBedTransferRes = z.infer<typeof apiResponseListQueryBedTransferResSchema>;

export const apiResponseListQueryCheckRecordResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(queryCheckRecordResSchema).nullable()
});
export type ApiResponseListQueryCheckRecordRes = z.infer<typeof apiResponseListQueryCheckRecordResSchema>;

export const apiResponseListQueryTenantCodeBaseResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.array(queryTenantCodeBaseResSchema).nullable()
});
export type ApiResponseListQueryTenantCodeBaseRes = z.infer<typeof apiResponseListQueryTenantCodeBaseResSchema>;

export const apiResponseObjectSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.unknown().nullable()
});
export type ApiResponseObject = z.infer<typeof apiResponseObjectSchema>;

export const reqCrudSaveSchema = z.object({
  systemNo: z.string().min(1),
  tableName: z.string().min(1),
  tableData: z.preprocess((val) => val === undefined ? null : val, z.array(z.unknown()).nullish())
});
export type ReqCrudSave = z.infer<typeof reqCrudSaveSchema>;

export const addHistoryReqSchema = z.object({
  encounterId: z.string().min(1),
  performDatetime: z.string().min(1),
  presentation: z.preprocess((val) => val === undefined ? null : val, presentationDtoSchema.nullish()),
  fhr: z.preprocess((val) => val === undefined ? null : val, z.number().nullish()),
  uterus: z.preprocess((val) => val === undefined ? null : val, uterusDtoSchema.nullish()),
  cervicalExam: z.preprocess((val) => val === undefined ? null : val, cervicalExamDtoSchema.nullish()),
  rupturedDetails: z.preprocess((val) => val === undefined ? null : val, rupturedDetailsReqDtoSchema.nullish()),
  laborStage: z.preprocess((val) => val === undefined ? null : val, z.enum(["WAITING_PHASE", "EARLY_PHASE", "ACTIVE_PHASE", "TRANSITION_PHASE", "PENDING_IN", "TRANSFERRING_OUT"]).nullish()),
  vitalList: z.preprocess((val) => val === undefined ? null : val, z.array(addVitalReqSchema).nullish())
});
export type AddHistoryReq = z.infer<typeof addHistoryReqSchema>;

export const addNursingNoteReqSchema = z.object({
  encounterId: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  patientId: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  performTime: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  note: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish())
});
export type AddNursingNoteReq = z.infer<typeof addNursingNoteReqSchema>;

export const printRecordReqSchema = z.object({
  jsonData: z.unknown()
});
export type PrintRecordReq = z.infer<typeof printRecordReqSchema>;

export const apiResponseAddBedTransferResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: addBedTransferResSchema.nullable()
});
export type ApiResponseAddBedTransferRes = z.infer<typeof apiResponseAddBedTransferResSchema>;

export const addBedTransferReqSchema = z.object({
  encounterId: z.string().min(1),
  toBedNo: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  fromBedNo: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  status: z.preprocess((val) => val === undefined ? null : val, z.enum(["DRAFT", "CONFIRM", "COMPLETE"]).nullish()),
  bedAddFee: z.preprocess((val) => val === undefined ? null : val, z.number().nullish()),
  selfPayConsentStatus: z.preprocess((val) => val === undefined ? null : val, z.enum(["NONE", "PENDING", "UNSIGNED", "SIGNED"]).nullish()),
  adDiagnosis: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  ipDiagnosis: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  procedureName: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  surgeryBodySite: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  examOrders: z.preprocess((val) => val === undefined ? null : val, z.array(examOrderCreateDtoSchema).nullish()),
  drips: z.preprocess((val) => val === undefined ? null : val, z.array(dripInfoCreateDtoSchema).nullish()),
  bloodTransfusions: z.preprocess((val) => val === undefined ? null : val, z.array(bloodTransfusionCreateDtoSchema).nullish()),
  drainageTubes: z.preprocess((val) => val === undefined ? null : val, z.array(drainTubeCreateDtoSchema).nullish()),
  specialMedication: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  specialMedicationMemo: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  medicalRecordFlag: z.preprocess((val) => val === undefined ? null : val, z.boolean().nullish()),
  oldMedicalRecordFlag: z.preprocess((val) => val === undefined ? null : val, z.boolean().nullish())
});
export type AddBedTransferReq = z.infer<typeof addBedTransferReqSchema>;

export const apiResponseQueryCheckRecordResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: queryCheckRecordResSchema.nullable()
});
export type ApiResponseQueryCheckRecordRes = z.infer<typeof apiResponseQueryCheckRecordResSchema>;

export const addCheckRecordReqSchema = z.object({
  bedTransferId: z.string().min(1),
  checkField: z.string().min(1),
  checkContent: z.preprocess((val) => val === undefined ? null : val, z.unknown().nullish())
});
export type AddCheckRecordReq = z.infer<typeof addCheckRecordReqSchema>;

export const apiResponseUpdateOrderExecutedResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: updateOrderExecutedResSchema.nullable()
});
export type ApiResponseUpdateOrderExecutedRes = z.infer<typeof apiResponseUpdateOrderExecutedResSchema>;

export const updateOrderExecutedReqSchema = z.object({
  orderId: z.string().min(1),
  orderStatus: z.enum(["ACTIVE", "ORDERED", "OVERTIME", "COMPLETED", "PROCESSED", "CANCELLED"]),
  performDatetime: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  orderStartDatetime: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  orderEndDatetime: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  sideEffectCode: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  sideEffectVital: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  notActiveReasonCode: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  notActiveReasonMemo: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  cancelReason: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  prnActiveReason: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish())
});
export type UpdateOrderExecutedReq = z.infer<typeof updateOrderExecutedReqSchema>;

export const updateHistoryReqSchema = z.object({
  encounterId: z.string().min(1),
  performDatetime: z.string().min(1),
  presentation: z.preprocess((val) => val === undefined ? null : val, presentationDtoSchema.nullish()),
  fhr: z.preprocess((val) => val === undefined ? null : val, z.number().nullish()),
  uterus: z.preprocess((val) => val === undefined ? null : val, uterusDtoSchema.nullish()),
  cervicalExam: z.preprocess((val) => val === undefined ? null : val, cervicalExamDtoSchema.nullish()),
  rupturedDetails: z.preprocess((val) => val === undefined ? null : val, rupturedDetailsReqDtoSchema.nullish()),
  laborStage: z.preprocess((val) => val === undefined ? null : val, z.enum(["WAITING_PHASE", "EARLY_PHASE", "ACTIVE_PHASE", "TRANSITION_PHASE", "PENDING_IN", "TRANSFERRING_OUT"]).nullish()),
  id: z.string().min(1),
  vitalList: z.preprocess((val) => val === undefined ? null : val, z.array(vitalsDtoSchema).nullish()),
  lockVersion: z.number()
});
export type UpdateHistoryReq = z.infer<typeof updateHistoryReqSchema>;

export const updateNursingNoteReqSchema = z.object({
  id: z.string().min(1),
  performTime: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  note: z.string().min(1)
});
export type UpdateNursingNoteReq = z.infer<typeof updateNursingNoteReqSchema>;

export const apiResponseVoidSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.unknown().nullable()
});
export type ApiResponseVoid = z.infer<typeof apiResponseVoidSchema>;

export const updatePatientSpecialStatusReqSchema = z.object({
  patientSpecialCondition: z.preprocess((val) => val === undefined ? null : val, z.enum(["NORMAL", "URGENT", "HIGH_RISK", "ATTENTION"]).nullish()),
  note: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish())
});
export type UpdatePatientSpecialStatusReq = z.infer<typeof updatePatientSpecialStatusReqSchema>;

export const apiResponseStringSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: z.string().nullable()
});
export type ApiResponseString = z.infer<typeof apiResponseStringSchema>;

export const apiResponseUpdateBedTransferResSchema = z.object({
  msg: z.string().nullable(),
  msgArgs: z.array(z.string()).nullable(),
  err: z.number().nullable(),
  statusCode: z.number().nullable(),
  data: updateBedTransferResSchema.nullable()
});
export type ApiResponseUpdateBedTransferRes = z.infer<typeof apiResponseUpdateBedTransferResSchema>;

export const updateBedTransferReqSchema = z.object({
  bedTransferId: z.string().min(1),
  toBedNo: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  fromBedNo: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  bedAddFee: z.preprocess((val) => val === undefined ? null : val, z.number().nullish()),
  selfPayConsentStatus: z.preprocess((val) => val === undefined ? null : val, z.enum(["NONE", "PENDING", "UNSIGNED", "SIGNED"]).nullish()),
  adDiagnosis: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  ipDiagnosis: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  procedureName: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  surgeryBodySite: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  examOrders: z.preprocess((val) => val === undefined ? null : val, z.array(examOrderDtoSchema).nullish()),
  drips: z.preprocess((val) => val === undefined ? null : val, z.array(dripInfoUpdateDtoSchema).nullish()),
  bloodTransfusions: z.preprocess((val) => val === undefined ? null : val, z.array(bloodTransfusionDtoSchema).nullish()),
  drainageTubes: z.preprocess((val) => val === undefined ? null : val, z.array(drainTubeDtoSchema).nullish()),
  specialMedication: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  specialMedicationMemo: z.preprocess((val) => val === "" || val === undefined ? null : val, z.string().nullish()),
  medicalRecordFlag: z.preprocess((val) => val === undefined ? null : val, z.boolean().nullish()),
  oldMedicalRecordFlag: z.preprocess((val) => val === undefined ? null : val, z.boolean().nullish()),
  lockVersion: z.preprocess((val) => val === undefined ? null : val, z.number().nullish())
});
export type UpdateBedTransferReq = z.infer<typeof updateBedTransferReqSchema>;

// Query Parameter Schemas

export const queryTenantCodeBaseByCodeTypeQuerySchema = z.object({
  tenantCodeBaseTypeEnum: z.enum(["BODY_SITE", "BODY_TEMPERATURE_METHOD", "BLOOD_TRANSFUSION", "DRAINAGE_TUBE", "NOT_ACTIVE_REASON", "SIDE_EFFECT", "EXAM_ORDER"])
});
export type QueryTenantCodeBaseByCodeTypeQuery = z.infer<typeof queryTenantCodeBaseByCodeTypeQuerySchema>;

export const queryBedTransferByEncounterIdQuerySchema = z.object({
  transferStatus: z.enum(["DRAFT", "CONFIRM", "COMPLETE"]).nullish()
});
export type QueryBedTransferByEncounterIdQuery = z.infer<typeof queryBedTransferByEncounterIdQuerySchema>;

export const queryDoctorQuerySchema = z.object({
  keyword: z.string().nullish()
});
export type QueryDoctorQuery = z.infer<typeof queryDoctorQuerySchema>;

export const queryDoctorOrderItemListQuerySchema = z.object({
  orderType: z.enum(["MEDICATION", "PRN_MEDICATION", "INJECTION", "LAB", "EXAM", "SURGERY"]).nullish()
});
export type QueryDoctorOrderItemListQuery = z.infer<typeof queryDoctorOrderItemListQuerySchema>;

export const queryQuerySchema = z.object({
  system: z.string(),
  tableName: z.string(),
  pageNum: z.number(),
  pageSize: z.number(),
  filters: z.string().nullish(),
  likes: z.string().nullish(),
  sorts: z.string().nullish()
});
export type QueryQuery = z.infer<typeof queryQuerySchema>;

export const queryStructureQuerySchema = z.object({
  system: z.string(),
  tableName: z.string()
});
export type QueryStructureQuery = z.infer<typeof queryStructureQuerySchema>;
